import {
  type GitBranchNameIssue,
  type GitWriteOptions,
  type GitWriteRepository,
  validateGitBranchName,
} from '@apm/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Dialog } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { errorMessage } from '../../../lib/error-message';
import { ApiRequestError } from '../../../lib/http';
import { type GitTargetRef, gitApi, gitKeys } from '../api';

export type GitWriteMode = 'branch' | 'changeRequest';

/**
 * Hộp thoại **Tạo nhánh / Tạo merge request** từ một việc — APM-GIT-k,
 * docs/plan-git-integration.md §8.4.
 *
 * Chọn kho (chỉ kho ghi được) · nhánh gốc / nhánh đích · tên sửa được. Dòng đầu
 * nói rõ **"sẽ tạo dưới tên `<account_name>`"**: lời gọi chạy bằng token của dự
 * án, nên trên máy chủ git người tạo là tài khoản ấy chứ không phải người bấm.
 *
 * Tạo xong ⇒ server xin "kéo ngay"; nhánh / MR hiện ở khối "Phát triển" khi lượt
 * kéo kế chạy xong (không ghi tắt vào bản sao — §8.4). Lời nhắn sau khi tạo nói
 * điều đó, để không ai tưởng là tạo hỏng.
 *
 * ⚠️ Không nhận asOf: form **ghi**, chỉ mở khi đang xem "bây giờ".
 */
export function GitWriteDialog({
  mode,
  projectId,
  target,
  options,
  branchName,
  sourceBranchOf,
  onClose,
}: {
  readonly mode: GitWriteMode;
  readonly projectId: string;
  readonly target: GitTargetRef;
  readonly options: GitWriteOptions;
  /** Tên nhánh gợi ý — cùng tên nút "Sao chép tên nhánh" chép. */
  readonly branchName: string;
  /** Nhánh của việc này ở một kho (đã kéo về) — nhánh nguồn mặc định của MR. */
  readonly sourceBranchOf: (repositoryId: string) => string | null;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const formId = useId();
  const writable = options.repositories.filter((repository) => repository.writable);
  const [repositoryId, setRepositoryId] = useState(writable[0]?.id ?? '');
  const repository: GitWriteRepository | undefined =
    writable.find((item) => item.id === repositoryId) ?? writable[0];

  const [name, setName] = useState(branchName);
  const [ref, setRef] = useState(repository?.defaultBranch ?? '');
  const [source, setSource] = useState((repository && sourceBranchOf(repository.id)) ?? branchName);
  const [targetBranch, setTargetBranch] = useState(repository?.defaultBranch ?? '');
  const [title, setTitle] = useState(options.changeRequestTitle);
  const [draft, setDraft] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const pickRepository = (id: string): void => {
    setRepositoryId(id);
    const next = writable.find((item) => item.id === id);
    setRef(next?.defaultBranch ?? '');
    setTargetBranch(next?.defaultBranch ?? '');
    setSource(sourceBranchOf(id) ?? branchName);
    setServerError(null);
  };

  const done = (): void => {
    void queryClient.invalidateQueries({ queryKey: gitKeys.project(projectId) });
    onClose();
  };
  const failed = (error: unknown): void => {
    // Lỗi của một ô (trùng tên, nhánh gốc không có, token không ghi được) hiện ngay
    // trong hộp, cạnh thứ cần sửa — một toast biến mất trước khi kịp đọc câu "cần scope gì".
    setServerError(errorMessage(error, t));
    if (!(error instanceof ApiRequestError)) toast.error(errorMessage(error, t));
  };

  const createBranch = useMutation({
    mutationFn: () =>
      gitApi.createBranch(projectId, repository!.id, {
        targetKind: target.kind,
        targetId: target.id,
        name: name.trim(),
        ref: ref.trim(),
      }),
    onSuccess: (created) => {
      toast.success(t('gitTab.write.branchCreated', { name: created.name }));
      done();
    },
    onError: failed,
  });
  const createChangeRequest = useMutation({
    mutationFn: () =>
      gitApi.createChangeRequest(projectId, repository!.id, {
        targetKind: target.kind,
        targetId: target.id,
        sourceBranch: source.trim(),
        targetBranch: targetBranch.trim(),
        title: title.trim(),
        draft,
      }),
    onSuccess: (created) => {
      toast.success(
        t(
          created.created
            ? 'gitTab.write.changeRequestCreated'
            : 'gitTab.write.changeRequestExisting',
          { number: created.number },
        ),
        {
          action: {
            label: t('gitTab.write.open'),
            onClick: () => window.open(created.webUrl, '_blank', 'noopener'),
          },
        },
      );
      done();
    },
    onError: failed,
  });
  const pending = createBranch.isPending || createChangeRequest.isPending;

  const issueText = (issue: GitBranchNameIssue | null): string | null =>
    issue ? t(`gitTab.write.nameIssue.${issue}`) : null;
  const fields =
    mode === 'branch'
      ? { name: validateGitBranchName(name), ref: validateGitBranchName(ref) }
      : {
          source: validateGitBranchName(source),
          target: validateGitBranchName(targetBranch),
          title: title.trim() === '' ? ('empty' as const) : null,
        };
  const sameBranches = mode === 'changeRequest' && source.trim() === targetBranch.trim();
  const invalid = Object.values(fields).some((issue) => issue !== null) || sameBranches;
  const shown = (issue: GitBranchNameIssue | null): string | null =>
    submitted ? issueText(issue) : null;

  const heading = t(
    mode === 'branch' ? 'gitTab.write.branchTitle' : 'gitTab.write.changeRequestTitle',
    {
      code: options.target.code,
    },
  );

  if (!repository) return null;

  const fieldError = (message: string | null): ReactNode =>
    message ? <span className="text-destructive">{message}</span> : null;

  return (
    <Dialog label={heading} onClose={onClose}>
      <form
        id={formId}
        className="flex flex-col gap-3"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          setServerError(null);
          if (invalid || pending) return;
          if (mode === 'branch') createBranch.mutate();
          else createChangeRequest.mutate();
        }}
      >
        <header className="flex flex-col gap-1">
          <h2 className="text-base font-medium">{heading}</h2>
          <p className="text-xs text-muted-foreground" data-testid="git-write-account">
            {repository.accountName
              ? t('gitTab.write.asAccount', {
                  account: repository.accountName,
                  host: repository.hostName,
                })
              : t('gitTab.write.asAccountUnknown', { host: repository.hostName })}
          </p>
        </header>

        {writable.length > 1 ? (
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            {t('gitTab.write.repository')}
            <select
              className="h-9 max-w-full rounded-[12px] border border-border-strong bg-background px-3 text-sm text-foreground"
              value={repository.id}
              onChange={(event) => pickRepository(event.target.value)}
            >
              {writable.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.fullPath}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p className="text-xs text-muted-foreground">
            {t('gitTab.write.repository')}: <span className="font-mono">{repository.fullPath}</span>
          </p>
        )}

        {mode === 'branch' ? (
          <>
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              {t('gitTab.write.branchName')}
              <Input
                autoFocus
                className="rounded-[12px] font-mono"
                value={name}
                spellCheck={false}
                aria-invalid={shown(fields.name ?? null) ? true : undefined}
                onChange={(event) => setName(event.target.value)}
              />
              <span>{t('gitTab.write.branchNameHint')}</span>
              {fieldError(shown(fields.name ?? null))}
            </label>
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              {t('gitTab.write.baseBranch')}
              <Input
                className="rounded-[12px] font-mono"
                value={ref}
                spellCheck={false}
                aria-invalid={shown(fields.ref ?? null) ? true : undefined}
                onChange={(event) => setRef(event.target.value)}
              />
              {fieldError(shown(fields.ref ?? null))}
            </label>
          </>
        ) : (
          <>
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              {t('gitTab.write.sourceBranch')}
              <Input
                autoFocus
                className="rounded-[12px] font-mono"
                value={source}
                spellCheck={false}
                aria-invalid={shown(fields.source ?? null) ? true : undefined}
                onChange={(event) => setSource(event.target.value)}
              />
              <span>{t('gitTab.write.sourceBranchHint')}</span>
              {fieldError(shown(fields.source ?? null))}
            </label>
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              {t('gitTab.write.targetBranch')}
              <Input
                className="rounded-[12px] font-mono"
                value={targetBranch}
                spellCheck={false}
                aria-invalid={shown(fields.target ?? null) ? true : undefined}
                onChange={(event) => setTargetBranch(event.target.value)}
              />
              {fieldError(shown(fields.target ?? null))}
              {submitted && sameBranches ? fieldError(t('gitTab.write.sameBranches')) : null}
            </label>
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              {t('gitTab.write.title')}
              <Input
                className="rounded-[12px]"
                value={title}
                maxLength={255}
                aria-invalid={submitted && title.trim() === '' ? true : undefined}
                onChange={(event) => setTitle(event.target.value)}
              />
              <span>{t('gitTab.write.titleHint')}</span>
              {submitted && title.trim() === '' ? fieldError(t('gitTab.write.titleEmpty')) : null}
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="ios-switch mt-0.5 shrink-0"
                checked={draft}
                onChange={(event) => setDraft(event.target.checked)}
              />
              <span className="flex flex-col gap-0.5">
                {t('gitTab.write.draft')}
                <span className="text-xs text-muted-foreground">{t('gitTab.write.draftHint')}</span>
              </span>
            </label>
          </>
        )}

        {serverError ? (
          <p role="alert" className="text-xs text-destructive" data-testid="git-write-error">
            {serverError}
          </p>
        ) : null}

        <p className="text-[11px] text-muted-foreground">{t('gitTab.write.afterCreate')}</p>

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" size="sm" disabled={pending}>
            {pending
              ? t('gitTab.write.creating')
              : t(
                  mode === 'branch'
                    ? 'gitTab.write.submitBranch'
                    : 'gitTab.write.submitChangeRequest',
                )}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
